import axios, { type AxiosResponse } from 'axios';
import i18next from 'i18next';

import { isJson } from '@utils/JsonUtils';
import SnackBarUtils from '@utils/SnackBarUtils';
import { getGuestUserData } from '@utils/GeneralUtils';
import { errorLogger } from '@utils/axios/ErrorLogging';
import { ERROR_DETAILS_MAP, PROFILE_ERROR } from '@constants/ErrorConstants';
import { getUrlParam, setUrlParam } from '@utils/URLParamUtil';
import { skipToastMessagesMsgList } from '@utils/profile/ProfileUtils';

export const guestAxiosEnv = {
  podName: '' as string,
  currentDomain: '' as string | null | undefined
};
export const setPodName = (podname: string) => {
  guestAxiosEnv.podName = podname;
};
export const setCurrentDomain = (currentdomain: string | undefined) => {
  guestAxiosEnv.currentDomain = currentdomain;
};

export const createDownloadElem = (downloadUrl: string, callBack?: () => void) => {
  const link = document.createElement('a');
  link.href = `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}podName=${guestAxiosEnv.podName}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  callBack?.();
};

// eslint-disable-next-line import/no-anonymous-default-export
export default () => {
  // currentDomain = currentDomain ? currentDomain : localStorage.getItem('curentDomain');
  const baseURL = '/rfpserver/';

  const axiosInstance = axios.create({
    baseURL
  });

  const checkVersions = function (response: AxiosResponse) {
    const responseHeaders = response.headers;
    if (responseHeaders['rfpio-version']) {
      const version = responseHeaders['rfpio-version'] ? JSON.stringify(responseHeaders['rfpio-version']) : '0';
      const existingVersion = localStorage.getItem('version');
      if (parseInt(JSON.parse(version), 10) > parseInt(JSON.parse(existingVersion || '0'), 10)) {
        localStorage.setItem('version', version);
        if (existingVersion != null) {
          window.location.reload();
        }
      }
    }
  };

  const showErrorToast = (obj: { [k: string]: string | undefined }) => {
    if (skipToastMessagesMsgList.includes(obj.additional_message || '')) {
      return;
    }
    let { message } = obj;
    if (obj.additional_message) {
      if (obj.additional_message.includes(':')) {
        const splitMessage = obj.additional_message.split(':');
        const key = splitMessage[0].trim();
        const value = splitMessage.slice(1).join(':');
        message = `${i18next.t(key)} : ${value}`;
      } else {
        message = i18next.t(obj.additional_message);
      }
    }
    SnackBarUtils.error(message);
  };

  axiosInstance.interceptors.request.use(
    function (config) {
      // config.headers['Access-Control-Allow-Origin'] = '*';
      const currentUser = getGuestUserData();
      const isPublicUser: boolean = !window.location.href.includes('/profile-view');
      const isAccessVerified: boolean = JSON.parse(sessionStorage.getItem('guestAccessVerified') || 'false');
      if (
        !config.url?.includes('/guest_v3/init') &&
        !config.url?.includes('/guest_v3/send-otp') &&
        !config.url?.includes('/guest_v3/validate-otp') &&
        !config.url?.includes('/guest_v3/resend-otp') &&
        !config.url?.includes('/auth-custom-domain/verify') &&
        currentUser.accessKey &&
        (!isPublicUser || isAccessVerified)
      ) {
        config.headers.authorization = `Bearer ${currentUser.accessKey}`;
        config.headers['guest-key'] = currentUser.guestKey;
      }
      if (guestAxiosEnv.podName && !config.url?.includes('/auth-profiles')) {
        config.headers['pod-name'] = guestAxiosEnv.podName;
        // config.headers['X-Custom-Domain'] = 'profile_center';
      }

      const request = sessionStorage.getItem('requestVO');
      if (request && isJson(request) && currentUser?.companyGuestVO) {
        const requestVO = JSON.parse(request);
        if (requestVO.mailId !== currentUser.companyGuestVO.guest && config.url?.includes('guest_v3/public')) {
          window.location.reload();
          return new Promise(() => {});
        }
      }

      return config;
    },
    error => {
      errorLogger(JSON.stringify(error), true, 'Caught from request in the method axios.ts');
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    response =>
      new Promise(resolve => {
        // checkSessionTimeout(response);
        checkVersions(response);
        resolve(response);
      }),
    error => {
      if (!error.response) {
        return new Promise((resolve, reject) => {
          errorLogger(JSON.stringify(error), true, 'Caught from response in the method axios.ts');
          reject(error);
        });
      }

      const urlError: string | null = getUrlParam('error');
      if (
        error.response.config?.url?.includes('guest_v3') &&
        !error.response.config?.url?.includes('/track-usage-time') &&
        !urlError &&
        error.response?.data &&
        ERROR_DETAILS_MAP[error.response.data]
      ) {
        setUrlParam('error', error.response.data as PROFILE_ERROR);
        window.location.reload();
      }

      checkVersions(error.response);
      if (error.response.status === 401) {
        if (error.response.config?.url?.includes('/validate-access-token')) {
          const currentUser = getGuestUserData();
          if (currentUser.guestKey) {
            const newurl: string = `/profile-view/${currentUser.guestKey}?podName=${guestAxiosEnv.podName}`;
            window.history.replaceState({ path: newurl }, '', newurl);
          }
          localStorage.removeItem('guestUserData');
        }
      } else if (error.response.status === 403) {
        console.log('403 Forbidden');
        setUrlParam('error', PROFILE_ERROR.INVALID_LINK);
        window.location.reload();
        return new Promise(resolve => {
          resolve(error.response);
        });
      } else if (error.response.status === 400) {
        console.log('400 Bad Request');
        if (error.response.data) {
          if (error.response.data.error_description) {
            showErrorToast({ message: error.response.data.error_description, additional_message: error.response.data.additional_message });
          }
          return new Promise(resolve => {
            resolve(error.response);
          });
        }
      } else if (error.response.status === 413) {
        console.log('413 (Request Entity Too Large)');
        if (error.response.statusText === 'Request Entity Too Large') {
          SnackBarUtils.error('File size exceeds.');
          return new Promise(resolve => {
            resolve(error.response);
          });
        }
      } else if (error.response.status === 440) {
        console.log('440 Handled Exception');
      } else if (error.response.status === 500) {
        console.log('500 Internal Server Error');
      } else if (error.response.status === 502 || error.response.status === 503 || error.response.status === 504) {
        SnackBarUtils.toast('An error occurred. Please try again later.');
      } else if (error.response.status === 404) {
        return new Promise((resolve, reject) => {
          reject(error);
        });
      } else {
        errorLogger(error.response.data, true, 'Caught from response in the method axios.ts');
        return new Promise((resolve, reject) => {
          reject(error);
        });
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
